/* eslint-disable react/jsx-one-expression-per-line */
import {
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import * as React from 'react'
import { useLang } from '~/hooks/useLang'
import InputMask from 'react-input-mask'
import { ChangeEvent } from 'react'
import { History, Phone, MoreVert } from '@mui/icons-material'
import { State, useHookstate, useState } from '@hookstate/core'
import { getOnlyNumbersInString } from '~/utils/strings'
import useSystemState from '~/features/System/stores/SystemState'
import CancelIcon from '@mui/icons-material/Cancel'
import { hasIncidentByPhoneAndEntryId, callBackPhoneService } from '~/features/Entry/services'
import useEntryState from '~/features/Entry/stores/EntryState'
import { IIncident } from '../../features/Entry/interfaces/IIncidentPhone'
import useMapState, { useMarkerState } from '~/features/Map/stores/MapState'
import { LatLng } from 'leaflet'
import useEntryCommentState from '~/features/Entry/stores/EntryCommentState'
import { adjustPositionLeft } from '~/features/Map/utils/repositionMarker'
import notify from '~/utils/notify'

type PhoneProps = {
  readonly readonly?: boolean
  readonly statePhone: State<string | null>
  readonly disabled: boolean
  readonly onClickButton?: () => void
  readonly disabledLastCalls?: boolean
  readonly phoneRequesterPlaceholderLabel?: boolean
  readonly showSubmenu?: boolean
  readonly onClickCall?: () => void
}

// eslint-disable-next-line max-lines-per-function
export default function PhoneTextField({
  readonly = false,
  statePhone,
  disabled,
  onClickButton = () => {},
  disabledLastCalls = false,
  phoneRequesterPlaceholderLabel = false,
  showSubmenu = false,
  onClickCall = () => {},
}: PhoneProps) {
  const { translate } = useLang()
  const { windowManager, systemConfig } = useSystemState()
  const { createMarker, markersState, updateMarker, forceViewMap } =
    useMarkerState()
  const { syncPreviousComments } = useEntryCommentState()
  const { entry } = useEntryState()
  const {
    id: entryId,
    typeId,
    cityId,
    districtId,
    location,
    name,
    incidentId,
    latitude,
    longitude,
  } = useState(entry())
  const existMarker = Object.entries(
    markersState()['locationRequester'].get()
  ).filter(([key, value]: any) => value?.id == incidentId.get())
  const phone = useState(statePhone)
  const phoneValue = phone.get() == null ? '' : `${phone.get()}`
  const maskCelPhone = '(99) 99999-9999'
  const maskPhone = '(99) 9999-9999'
  const mask = useHookstate(maskCelPhone)
  const Input = InputMask as any
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleLastCalls = () => {
    onClickButton()
    handleCloseMenu()
  }

  const handleCall = () => {
    callBackPhoneService(phoneValue)
      .then(() => {
        notify({
          message: translate('Call initiated successfully'),
          type: 'success',
        })
        onClickCall()
      })
      .catch(() => {
        notify({
          message: translate('Error initiating call'),
          type: 'error',
        })
      })
      .finally(() => {
        handleCloseMenu()
      })
  }

  const defineMask = (number) => {
    if (number <= 10) {
      mask.set(maskPhone)
    } else {
      mask.set(maskCelPhone)
    }
  }
  // Função para limpar o campo
  const handleClear = () => {
    phone.set(null) // Limpa o estado do telefone
  }

  const updateEntryForm = ({ data }) => {
    data.type_id ? typeId.set(data.type_id) : null
    data.city_id ? cityId.set(data.city_id) : null
    data.district_id ? districtId.set(data.district_id) : null
    data.address ? location.set(data.address) : null
    data.latitude ? latitude.set(data.latitude) : null
    data.longitude ? longitude.set(data.longitude) : null
    !name.get() ? name.set(data.requester) : null
  }

  const createMarkerlocationRequester = ({ data }) => {
    const hasIncident: boolean = data.has_incident
    const incidentInfo = data.incident_info

    if (hasIncident) {
      updateEntryForm({ data: incidentInfo })
      !incidentId.get() && syncPreviousComments(entryId.get())

      incidentId.set(incidentInfo.incident_id)

      const paramsEntry = {
        id: entryId.get(),
        isCurrent: true,
        position: new LatLng(Number(latitude.get()), Number(longitude.get())),
        location: location.get(),
        status: 1,
      }

      createMarker({
        markerType: 'entry',
        markerIdentifier: paramsEntry.id,
        ...paramsEntry,
      })

      const adjustedPosition = adjustPositionLeft(
        incidentInfo.latitude,
        incidentInfo.longitude,
        30 // 30 metros para a esquerda
      )

      const paramsIncident = {
        id: incidentInfo.incident_id,
        isCurrent: true,
        location: incidentInfo.address,
        incidentId: incidentInfo.incident_id,
        position: adjustedPosition,
      }

      if (!existMarker.length) {
        createMarker({
          markerType: 'locationRequester',
          markerIdentifier: incidentInfo.incident_id,
          ...paramsIncident,
        })
      } else {
        updateMarker({
          markerType: 'locationRequester',
          markerIdentifier: incidentInfo.incident_id,
          attribute: 'location',
          value: incidentInfo.address,
          ...paramsIncident,
        })
        updateMarker({
          markerType: 'locationRequester',
          markerIdentifier: incidentInfo.incident_id,
          attribute: 'position',
          value: adjustedPosition,
          ...paramsIncident,
        })
      }

      if (!incidentId.get()) {
        forceViewMap().zoom.set(17)
        forceViewMap().position.set(adjustedPosition)
      }
    }
  }

  React.useEffect(() => {
    const phoneLength = phoneValue.length
    const isEntryPage = window.location.pathname === '/entry'
    
    if (phoneLength <= 9) {
      mask.set(maskCelPhone)
    }

    if (!isEntryPage) return
    if (phoneLength >= 10) {
      windowManager()['EntryLogModal'].open.set(true)
    }

    if (phoneLength > 10) {
      hasIncidentByPhoneAndEntryId({
        entryId: entryId.get(),
        phone: phoneValue,
      }).then((response) => {
        createMarkerlocationRequester({ data: response })
      })
    }
  }, [phoneValue])

  if (!readonly) {
    return (
      <Input
        mask={mask.get()}
        value={phoneValue}
        onBlur={() => defineMask(phoneValue.length)}
        maskChar=""
        onChange={(changeEvent: ChangeEvent<HTMLInputElement>) => {
          phone.set(getOnlyNumbersInString(changeEvent.target.value))
        }}
        disabled={disabled}
      >
        <TextField
          required={false}
          fullWidth
          size="small"
          id="telephone"
          label={
            phoneRequesterPlaceholderLabel
              ? translate('Requester Phone')
              : translate('Telephone')
          }
          name="telephone"
          autoComplete="tel"
          placeholder={
            phoneRequesterPlaceholderLabel
              ? translate('Requester Phone')
              : translate('Telephone')
          }
          disabled={disabled}
          InputProps={{
            sx: { paddingRight: 0 },
            endAdornment: (
              <InputAdornment position="end" sx={{ padding: 0, margin: 0 }}>
                {phoneValue && ( // Mostra o ícone de limpar apenas se houver conteúdo
                  <IconButton
                    onClick={handleClear}
                    aria-label="clear"
                    edge="end"
                    size="small"
                    disabled={disabled}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                )}
                {!disabledLastCalls && systemConfig().telephonyIntegrationEnabled.get() === '0' && (
                  <Tooltip title={translate('Last Calls')}>
                    <span>
                      <IconButton
                        onClick={() => onClickButton()}
                        color="success"
                        aria-label="directions"
                        disabled={phoneValue.length >= 10 ? false : true}
                      >
                        <History />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {!disabledLastCalls && systemConfig().telephonyIntegrationEnabled.get() === '1' && (
                  <>
                    <Tooltip title={translate('Options')}>
                      <span style={{ display: 'flex', alignItems: 'flex-end'}}>
                        <IconButton
                          onClick={handleClickMenu}
                          color="success"
                          aria-label="menu"
                          disabled={phoneValue.length >= 10 ? false : true}
                        >
                          <MoreVert />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleCloseMenu}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem onClick={handleLastCalls}>
                        <ListItemIcon>
                          <History fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{translate('Last Calls')}</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={handleCall}>
                        <ListItemIcon>
                          <Phone fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{translate('Make Call')}</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Input>
    )
  } else {
    return (
      <Typography>
        {translate('Phone')}:{phoneValue ?? translate('No information')}
      </Typography>
    )
  }
}
